/**
 * The one error boundary every screen in `components/errors` is built on.
 *
 * It catches what its children throw while rendering, shows the fallback it
 * is given, and clears it on "Try again" or when one of `resetKeys` changes
 * (the address, for a page: following any link out of a broken page works).
 *
 * Stale code (a deploy, or a module saved half way) gets one automatic reload,
 * guarded in sessionStorage (`stale.ts`); meanwhile `reloading` is shown rather
 * than an error. A section passes `autoReload={false}`: the rest of its page
 * may hold unsaved work, so it offers the reload instead of making it.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { isStaleCodeError } from "./classify";
import { reloadGraceLeft, reloadOnceForStaleCode, reloadPending, staleReloadRecently } from "./stale";

export interface FallbackProps {
  error: unknown;
  /** When it was caught. */
  at: Date;
  /** Stale code: only a reload helps, so a fallback offers that alone. */
  stale: boolean;
  /** Clear the error and render the children again. */
  reset: () => void;
}

export interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback: (props: FallbackProps) => ReactNode;
  /** Shown while an automatic reload for stale code is on its way. */
  reloading?: ReactNode;
  /** A shown fallback clears when one of these changes. */
  resetKeys?: readonly unknown[];
  /** Reload once, by itself, on stale code. Default true. */
  autoReload?: boolean;
  /** Called on "Try again" and on a reset by key, after the error is cleared. */
  onReset?: () => void;
  onError?: (error: unknown, info: ErrorInfo) => void;
}

interface State {
  caught: boolean;
  error: unknown;
  at: Date | null;
  stale: boolean;
  /** Stale, and no automatic reload in the last minute: one is about to start. */
  reloadDue: boolean;
}

const CLEAR: State = { caught: false, error: null, at: null, stale: false, reloadDue: false };

function keysChanged(a: readonly unknown[] = [], b: readonly unknown[] = []): boolean {
  return a.length !== b.length || a.some((value, i) => !Object.is(value, b[i]));
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = CLEAR;
  private graceTimer: number | undefined;

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const stale = isStaleCodeError(error);
    return { caught: true, error, at: new Date(), stale, reloadDue: stale && !staleReloadRecently() };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    if (this.state.reloadDue && this.props.autoReload !== false && !reloadOnceForStaleCode()) {
      this.setState({ reloadDue: false });
    }
    // A reload that has not happened after a few seconds was held up (a page
    // asked first, and the answer was to stay): stop waiting, show the screen.
    if (reloadPending()) {
      window.clearTimeout(this.graceTimer);
      this.graceTimer = window.setTimeout(() => this.setState({ reloadDue: false }), reloadGraceLeft() + 50);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps, prevState: State): void {
    // Only once the fallback is up: the render that caught the error may itself
    // be the one where the keys changed.
    if (this.state.caught && prevState.caught && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.graceTimer);
  }

  reset = (): void => {
    window.clearTimeout(this.graceTimer);
    this.setState(CLEAR);
    this.props.onReset?.();
  };

  render(): ReactNode {
    const { caught, error, at, stale, reloadDue } = this.state;
    if (!caught) return this.props.children;
    // The page is on its way out (this reload, or one asked a moment ago, which
    // can surface here as a follow-on error): no error screen for that instant.
    if (reloadPending() || (reloadDue && this.props.autoReload !== false)) return this.props.reloading ?? null;
    return this.props.fallback({ error, at: at ?? new Date(), stale, reset: this.reset });
  }
}
